import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { motion } from "motion/react";
import { useState } from "react";
import { z } from "zod";
import { Logo } from "@/components/ui";
import { anmelden, passwortSetzen } from "../auth";

/**
 * Neues Passwort setzen (T072) — das Ziel des Links aus Zugangs-Mail und
 * „Passwort vergessen". Der Token steht in der Adresse; nach dem Setzen
 * meldet die Seite direkt an, damit niemand das frische Passwort gleich
 * nochmal tippen muss.
 */
const suchSchema = z.object({
	token: z.string().optional(),
});

export const Route = createFileRoute("/passwort-zuruecksetzen")({
	validateSearch: suchSchema,
	component: PasswortSetzenSeite,
});

const MIN_LAENGE = 12;

function PasswortSetzenSeite() {
	const { token } = Route.useSearch();
	const { queryClient } = Route.useRouteContext();
	const navigate = useNavigate();
	const [email, setEmail] = useState("");
	const [passwort, setPasswort] = useState("");
	const [wiederholung, setWiederholung] = useState("");
	const [laeuft, setLaeuft] = useState(false);
	const [fehler, setFehler] = useState<string | null>(null);

	const absenden = async () => {
		if (token === undefined || token === "") {
			setFehler("Der Link ist unvollständig — bitte den Link aus der Mail komplett öffnen.");
			return;
		}
		if (passwort.length < MIN_LAENGE) {
			setFehler(`Das Passwort braucht mindestens ${String(MIN_LAENGE)} Zeichen.`);
			return;
		}
		if (passwort !== wiederholung) {
			setFehler("Die beiden Passwörter stimmen nicht überein.");
			return;
		}
		setLaeuft(true);
		setFehler(null);
		try {
			const ergebnis = await passwortSetzen({ token, neuesPasswort: passwort });
			if (!ergebnis.ok) {
				setFehler(ergebnis.message);
				return;
			}
			// Direkt anmelden — mit der E-Mail, die der Mensch hier angibt. Klappt
			// das nicht (Tippfehler), landet er einfach auf der Anmeldeseite.
			const anmeldung =
				email.trim() === ""
					? { ok: false as const }
					: await anmelden({ queryClient, email: email.trim(), password: passwort });
			await navigate({ to: anmeldung.ok ? "/" : "/login" });
		} finally {
			setLaeuft(false);
		}
	};

	return (
		<div className="min-h-dvh grid place-items-center bg-bg p-6">
			<motion.div
				initial={{ opacity: 0, y: 14 }}
				animate={{ opacity: 1, y: 0 }}
				className="w-full max-w-sm"
			>
				<div className="mb-8">
					<Logo />
				</div>
				<h1 className="font-display text-display font-medium text-ink tracking-tight">
					Neues Passwort setzen
				</h1>
				<p className="text-ink-soft mt-1.5">
					Mindestens {MIN_LAENGE} Zeichen — gern ein Satz, den du dir merkst.
				</p>
				{token === undefined || token === "" ? (
					<p
						role="alert"
						className="mt-6 rounded-xl border border-negative/40 bg-negative/10 px-3 py-2 text-sm text-ink"
					>
						Dieser Link ist unvollständig.{" "}
						<Link to="/passwort-vergessen" className="font-medium underline">
							Neuen Link anfordern
						</Link>
					</p>
				) : (
					<form
						className="mt-7 space-y-3"
						onSubmit={(event) => {
							event.preventDefault();
							void absenden();
						}}
					>
						<label htmlFor="email" className="block">
							<span className="text-sm font-medium text-ink-soft">Deine E-Mail-Adresse</span>
							<input
								id="email"
								type="email"
								autoComplete="username"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								className="input"
							/>
						</label>
						<label htmlFor="passwort" className="block">
							<span className="text-sm font-medium text-ink-soft">Neues Passwort</span>
							<input
								id="passwort"
								type="password"
								required
								minLength={MIN_LAENGE}
								autoComplete="new-password"
								value={passwort}
								onChange={(e) => setPasswort(e.target.value)}
								className="input"
							/>
						</label>
						<label htmlFor="wiederholung" className="block">
							<span className="text-sm font-medium text-ink-soft">Noch einmal</span>
							<input
								id="wiederholung"
								type="password"
								required
								autoComplete="new-password"
								value={wiederholung}
								onChange={(e) => setWiederholung(e.target.value)}
								className="input"
							/>
						</label>
						{fehler !== null && (
							<p
								role="alert"
								className="rounded-xl border border-negative/40 bg-negative/10 px-3 py-2 text-sm text-ink"
							>
								{fehler}
							</p>
						)}
						<button
							type="submit"
							disabled={laeuft}
							className="btn-primary w-full mt-4 h-12 text-body"
						>
							{laeuft ? "Einen Moment…" : "Passwort speichern & anmelden"}
						</button>
					</form>
				)}
			</motion.div>
		</div>
	);
}
